import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Menu, X, ChevronDown, ChevronRight, Sun, Moon, User, Check,
  Landmark, GraduationCap, Cake, Stethoscope, Repeat, CreditCard, 
  Mountain, HelpCircle, Phone, FileText, Loader2
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useTranslations } from '../context/TranslationsContext';
import Logo from './Logo';
import { getLocalizedPath, getHomePath, switchLanguagePath, isPathActive, setDynamicLanguages } from '../utils/routes';
import FlagIcon from './FlagIcon';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Fallback languages in case API fails
const FALLBACK_LANGUAGES = [
  { code: 'pl', nativeName: 'Polski', flag: '🇵🇱' },
  { code: 'en', nativeName: 'English', flag: '🇬🇧' },
];

const FloatingNavbar = () => {
  const { t, i18n } = useTranslation();
  const { td } = useTranslations(); // Database translations
  const { theme, toggleTheme } = useTheme();
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activitiesOpen, setActivitiesOpen] = useState(false);
  const [campsOpen, setCampsOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  // Mobile submenu states
  const [mobileActivitiesOpen, setMobileActivitiesOpen] = useState(false);
  const [mobileCampsOpen, setMobileCampsOpen] = useState(false);
  
  // Dynamic languages from API
  const [languages, setLanguages] = useState(FALLBACK_LANGUAGES);
  const [languagesLoading, setLanguagesLoading] = useState(true);
  
  // Smart translate: try database first, fallback to i18next
  const tr = (key, fallback = null) => {
    const dbValue = td(key);
    // If td returns the key itself (no translation found), use i18next
    if (dbValue === key) {
      return t(key, fallback || key);
    }
    return dbValue;
  };

  // Fetch active languages from API
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await axios.get(`${API}/api/languages`);
        if (response.data && response.data.length > 0) {
          // Update local state
          setLanguages(response.data.map(lang => ({
            code: lang.code,
            nativeName: lang.nativeName,
            flag: lang.flag
          })));
          // Also update the global routes.js cache for URL mappings
          setDynamicLanguages(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch languages, using fallback:', error);
      } finally {
        setLanguagesLoading(false);
      }
    };
    fetchLanguages();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    // Navigate to equivalent page in new language
    const newPath = switchLanguagePath(langCode, location.pathname);
    navigate(newPath, { replace: true });
    setLangMenuOpen(false);
  };

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0] || FALLBACK_LANGUAGES[0];

  // Helper to get localized path
  const lp = (key) => getLocalizedPath(key, i18n.language);

  // Mobile nav structure - matches desktop exactly
  const mobileNavItems = [
    { label: tr('nav.park'), path: lp('park'), icon: Landmark },
    { 
      label: tr('nav.activities'), 
      icon: GraduationCap,
      expandable: true,
      stateKey: 'activities',
      children: [
        { label: tr('nav.classes'), path: lp('classes') },
        { label: tr('nav.birthdays'), path: lp('birthdays') },
        { label: tr('nav.physio'), path: lp('physio') }
      ]
    },
    { label: tr('nav.prices'), path: lp('prices'), icon: Repeat },
    { label: tr('nav.passes'), path: lp('passes'), icon: CreditCard },
    { 
      label: tr('nav.camps'), 
      icon: Mountain,
      expandable: true,
      stateKey: 'camps',
      children: [
        { label: 'Obóz BMX', path: 'https://bmxcamp.pl', external: true },
        { label: 'Obóz Hulajnogowy', path: 'https://scootivecamp.pl', external: true }
      ]
    },
    { label: tr('nav.faq'), path: lp('faq'), icon: HelpCircle },
    { label: tr('nav.contact'), path: lp('contact'), icon: Phone }
  ];

  // Desktop nav items (with dropdowns)
  const desktopNavItems = [
    { label: tr('nav.park'), path: lp('park') },
    { 
      label: tr('nav.activities'), 
      children: [
        { label: tr('nav.classes'), path: lp('classes') },
        { label: tr('nav.birthdays'), path: lp('birthdays') },
        { label: tr('nav.physio'), path: lp('physio') }
      ]
    },
    { label: tr('nav.prices'), path: lp('prices') },
    { label: tr('nav.passes'), path: lp('passes') },
    { 
      label: tr('nav.camps'), 
      children: [
        { label: 'Obóz BMX', path: 'https://bmxcamp.pl', external: true },
        { label: 'Obóz Hulajnogowy', path: 'https://scootivecamp.pl', external: true }
      ]
    },
    { label: tr('nav.faq'), path: lp('faq') },
    { label: tr('nav.contact'), path: lp('contact') }
  ];

  const handleLogout = async () => {
    await logout();
    navigate(getHomePath(i18n.language));
  };

  const toggleMobileSubmenu = (key) => {
    if (key === 'activities') {
      setMobileActivitiesOpen(!mobileActivitiesOpen);
    } else if (key === 'camps') {
      setMobileCampsOpen(!mobileCampsOpen);
    }
  };

  const isMobileSubmenuOpen = (key) => {
    if (key === 'activities') return mobileActivitiesOpen;
    if (key === 'camps') return mobileCampsOpen;
    return false;
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed left-0 right-0 z-50 px-4 transition-all duration-300 ${
          isScrolled ? 'top-2' : 'top-4'
        }`}
      >
        <div className="floating-nav max-w-6xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          {/* Logo */}
          <Link to={getHomePath(i18n.language)} className="flex items-center gap-2" data-testid="nav-logo">
            <Logo className="ml-4 h-8 w-auto" variant={theme === 'dark' ? 'light' : 'dark'} />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {desktopNavItems.map((item, index) => (
              item.children ? (
                <div 
                  key={index} 
                  className="relative"
                  onMouseEnter={() => item.label === tr('nav.activities') ? setActivitiesOpen(true) : setCampsOpen(true)}
                  onMouseLeave={() => item.label === tr('nav.activities') ? setActivitiesOpen(false) : setCampsOpen(false)}
                >
                  <button 
                    className="flex items-center gap-1 px-4 py-2 text-sm font-medium hover:text-primary transition-colors"
                    data-testid={`nav-${item.label.toLowerCase()}`}
                  >
                    {item.label}
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  <AnimatePresence>
                    {(item.label === tr('nav.activities') ? activitiesOpen : campsOpen) && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="absolute top-full left-0 mt-2 rounded-2xl p-2 min-w-[200px] bg-white/90 dark:bg-black/80 border border-black/10 dark:border-white/10 shadow-2xl"
                        style={{ 
                          backdropFilter: 'blur(24px)',
                          WebkitBackdropFilter: 'blur(24px)'
                        }}
                      >
                        {item.children.map((child, childIndex) => (
                          child.external ? (
                            <a
                              key={childIndex}
                              href={child.path}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block px-4 py-3 text-sm text-gray-800 dark:text-white hover:bg-primary/10 rounded-xl transition-colors"
                              data-testid={`nav-${child.label.toLowerCase().replace(/\s/g, '-')}`}
                            >
                              {child.label}
                            </a>
                          ) : (
                            <Link
                              key={childIndex}
                              to={child.path}
                              className="block px-4 py-3 text-sm text-gray-800 dark:text-white hover:bg-primary/10 rounded-xl transition-colors"
                              data-testid={`nav-${child.label.toLowerCase().replace(/\s/g, '-')}`}
                            >
                              {child.label}
                            </Link>
                          )
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link
                  key={index}
                  to={item.path}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    location.pathname === item.path ? 'text-primary' : 'hover:text-primary'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </Link>
              )
            ))}
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center gap-2">
            {/* Language Selector */}
            <div 
              className="relative"
              onMouseEnter={() => setLangMenuOpen(true)}
              onMouseLeave={() => setLangMenuOpen(false)}
            >
              <button
                className="p-2 hover:text-primary transition-colors flex items-center gap-1"
                data-testid="language-toggle"
                aria-label="Select language"
              >
                <FlagIcon code={currentLang.code} size={20} />
                <ChevronDown className="w-3 h-3" />
              </button>
              <AnimatePresence>
                {langMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute top-full right-0 mt-2 rounded-xl p-2 min-w-[150px] shadow-2xl bg-white/90 dark:bg-black/80 border border-black/10 dark:border-white/10"
                    style={{ 
                      backdropFilter: 'blur(24px)',
                      WebkitBackdropFilter: 'blur(24px)'
                    }}
                  >
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors ${
                          i18n.language === lang.code ? 'bg-primary/20 text-primary' : 'text-gray-800 dark:text-white hover:bg-black/5 dark:hover:bg-white/10'
                        }`}
                        data-testid={`lang-${lang.code}`}
                      >
                        <FlagIcon code={lang.code} size={18} />
                        <span>{lang.nativeName}</span>
                        {i18n.language === lang.code && <Check className="w-4 h-4 ml-auto text-primary" />}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Theme Toggle - temporarily disabled */}
            {/* <button
              onClick={toggleTheme}
              className="p-2 hover:text-primary transition-colors"
              data-testid="theme-toggle"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button> */}

            {/* User Menu */}
            {isAuthenticated ? (
              <div 
                className="relative"
                onMouseEnter={() => setUserMenuOpen(true)}
                onMouseLeave={() => setUserMenuOpen(false)}
              >
                <button 
                  className="p-2 hover:text-primary transition-colors"
                  data-testid="user-menu-button"
                >
                  <User className="w-5 h-5" />
                </button>
                <AnimatePresence>
                  {userMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="absolute top-full right-0 mt-2 rounded-2xl p-2 min-w-[180px] shadow-2xl bg-white/90 dark:bg-black/80 border border-black/10 dark:border-white/10"
                      style={{ 
                        backdropFilter: 'blur(24px)',
                        WebkitBackdropFilter: 'blur(24px)'
                      }}
                    >
                      <div className="px-4 py-2 border-b border-black/10 dark:border-white/10">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{user?.firstName}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{user?.email}</p>
                      </div>
                      <Link
                        to={lp('account')}
                        className="block px-4 py-3 text-sm text-gray-800 dark:text-white hover:bg-black/5 dark:hover:bg-white/10 rounded-xl transition-colors"
                        data-testid="nav-account"
                      >
                        {tr('nav.account')}
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-3 text-sm hover:bg-primary/10 rounded-xl transition-colors text-red-500 dark:text-red-400"
                        data-testid="nav-logout"
                      >
                        {tr('nav.logout')}
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link
                to={lp('login')}
                className="hidden md:block px-4 py-2 text-sm font-medium hover:text-primary transition-colors"
                data-testid="nav-login"
              >
                {tr('nav.login')}
              </Link>
            )}

            {/* Book Button */}
            <Link
              to={lp('book')}
              className="btn-primary text-sm py-2 px-6 hidden md:block"
              data-testid="nav-book-button"
            >
              {tr('nav.book')}
            </Link>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-full hover:bg-primary/10 transition-colors"
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu - White background from top, under navbar */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[45] lg:hidden flex flex-col pt-[72px] bg-white"
          >
            {/* Menu Items - Clean list without icons */}
            <div className="flex-1 overflow-y-auto px-6 pt-6">
              <nav className="space-y-0">
                {mobileNavItems.map((item, index) => {
                  // Expandable items (Aktywności, Obozy)
                  if (item.expandable) {
                    const isOpen = isMobileSubmenuOpen(item.stateKey);
                    return (
                      <div key={index} className="border-b border-gray-200">
                        <button
                          onClick={() => toggleMobileSubmenu(item.stateKey)}
                          className="w-full flex items-center justify-between py-3 group"
                          data-testid={`mobile-nav-${item.stateKey}`}
                        >
                          <span className="text-lg font-normal text-gray-800 italic">{item.label}</span>
                          <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                        </button>
                        <AnimatePresence>
                          {isOpen && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              {item.children.map((child, childIndex) => (
                                child.external ? (
                                  <a
                                    key={childIndex}
                                    href={child.path}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-between py-3 pl-4 border-b border-gray-100 group"
                                    onClick={() => setMobileMenuOpen(false)}
                                  >
                                    <span className="text-base text-gray-600">{child.label}</span>
                                    <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-primary transition-colors" />
                                  </a>
                                ) : (
                                  <Link
                                    key={childIndex}
                                    to={child.path}
                                    className="flex items-center justify-between py-3 pl-4 border-b border-gray-100 group"
                                    onClick={() => setMobileMenuOpen(false)}
                                  >
                                    <span className="text-base text-gray-600">{child.label}</span>
                                    <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-primary transition-colors" />
                                  </Link>
                                )
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  }
                  
                  // Regular links - no icons, just text
                  return (
                    <Link
                      key={index}
                      to={item.path}
                      className="flex items-center justify-between py-3 border-b border-gray-200 group"
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                    >
                      <span className="text-lg font-normal text-gray-800 italic">{item.label}</span>
                    </Link>
                  );
                })}
              </nav>
            </div>

            {/* Bottom Section - CTA buttons and social icons - sticky at bottom */}
            <div className="flex-shrink-0 px-6 py-6 space-y-4 bg-white border-t border-gray-200">
              {/* Book Button - Primary CTA */}
              <Link
                to={lp('book')}
                onClick={() => setMobileMenuOpen(false)}
                className="block w-full py-4 text-white text-center font-semibold text-base rounded-full uppercase tracking-wider transition-colors"
                style={{ backgroundColor: '#7C3AED' }}
                data-testid="mobile-nav-book-btn"
              >
                {tr('nav.book', 'ZAREZERWUJ')}
              </Link>
              
              {/* Login/Register or Account Button - Secondary */}
              {isAuthenticated ? (
                <Link
                  to={lp('account')}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block w-full py-3 text-gray-800 text-center font-medium text-sm rounded-full border-2 border-gray-300 hover:border-gray-400 transition-colors"
                  data-testid="mobile-nav-account-btn"
                >
                  {tr('nav.account')}
                </Link>
              ) : (
                <Link
                  to={lp('login')}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block w-full py-3 text-gray-800 text-center font-medium text-sm rounded-full border-2 border-gray-300 hover:border-gray-400 transition-colors"
                  data-testid="mobile-nav-login-btn"
                >
                  {tr('nav.login', 'LOGOWANIE')} / {tr('nav.register', 'REJESTRACJA')}
                </Link>
              )}

              {/* Social Media Icons */}
              <div className="flex items-center justify-center gap-8">
                <a 
                  href="https://facebook.com/avepark" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-700 hover:text-primary transition-colors"
                  data-testid="social-facebook"
                >
                  <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a 
                  href="https://instagram.com/avepark" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-700 hover:text-primary transition-colors"
                  data-testid="social-instagram"
                >
                  <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                <a 
                  href="https://tiktok.com/@avepark" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-700 hover:text-primary transition-colors"
                  data-testid="social-tiktok"
                >
                  <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                  </svg>
                </a>
                <a 
                  href="https://youtube.com/@avepark" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-700 hover:text-primary transition-colors"
                  data-testid="social-youtube"
                >
                  <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FloatingNavbar;
